/**
 * ContentBoard - Kanban board voor content items per productiestatus
 * Drag-drop via @dnd-kit om items tussen kolommen te verplaatsen
 */
import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, IconButton, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem,
  InputLabel, FormControl, Autocomplete,
} from '@mui/material';
import { Add as AddIcon, DragIndicator as DragIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import {
  DndContext, DragOverlay, closestCorners, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import ContentItemDetail from './ContentItemDetail';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  IDEA: { label: 'Idee', color: '#6b7280', bg: '#f3f4f6' },
  CONCEPT: { label: 'Concept', color: '#8b5cf6', bg: '#ede9fe' },
  SHOOT_PLANNING: { label: 'Draaidag plannen', color: '#d97706', bg: '#fef3c7' },
  SHOOT_SCHEDULED: { label: 'Draaidag ingepland', color: '#ea580c', bg: '#ffedd5' },
  IN_PRODUCTION: { label: 'In Productie', color: '#2563eb', bg: '#dbeafe' },
  REVIEW: { label: 'Review', color: '#7c3aed', bg: '#ede9fe' },
  APPROVED: { label: 'Goedgekeurd', color: '#059669', bg: '#d1fae5' },
};

const KANBAN_COLUMNS = ['IDEA', 'CONCEPT', 'SHOOT_PLANNING', 'SHOOT_SCHEDULED', 'IN_PRODUCTION', 'REVIEW', 'APPROVED'];
const CHANNELS = ['Instagram Post', 'Instagram Story', 'Instagram Reels', 'Facebook Post', 'TikTok', 'YouTube', 'YouTube Shorts', 'LinkedIn', 'Threads Post'];
const CONTENT_TYPES = ['video', 'afbeelding', 'text_visual', 'animatie', 'anders'];

// ─── Card component (draggable) ─────────────────────────────────────────────

function ContentCard({ item, onClick, overlay = false }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id, disabled: overlay,
  });

  const style = overlay ? {} : {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  const channels = item.channels || [];

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      variant="outlined"
      onClick={() => onClick?.(item)}
      sx={{
        px: 1, py: 0.7, mb: 0.5, cursor: 'pointer', borderLeft: `3px solid ${STATUS_CONFIG[item.status]?.color || '#ccc'}`,
        '&:hover': { borderColor: '#6366f1', boxShadow: '0 1px 4px rgba(99,102,241,0.12)' },
        fontSize: '0.78rem',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Box {...attributes} {...listeners} sx={{ color: '#d1d5db', cursor: 'grab', display: 'flex', mt: 0.2, flexShrink: 0 }}>
          <DragIcon sx={{ fontSize: 12 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', lineHeight: 1.3 }}>
            {item.title || 'Naamloos'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.3 }}>
            {channels.length > 0 ? (
              <Typography variant="caption" sx={{ color: '#6366f1', fontSize: '0.65rem' }}>
                {channels.slice(0, 2).join(', ')}{channels.length > 2 ? ` +${channels.length - 2}` : ''}
              </Typography>
            ) : <span />}
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', flexShrink: 0 }}>
              {item.plannedDate ? new Date(item.plannedDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : ''}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

// ─── Droppable column ───────────────────────────────────────────────────────

function KanbanColumn({ columnId, items, onCardClick }) {
  const { setNodeRef } = useDroppable({ id: columnId });
  const cfg = STATUS_CONFIG[columnId];
  const ids = items.map((i) => i.id);

  return (
    <Box
      ref={setNodeRef}
      sx={{ flex: 1, minWidth: 180, maxWidth: 240, display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 0.5 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cfg.color }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{cfg.label}</Typography>
        <Chip label={items.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
      </Box>
      <Box sx={{ flex: 1, bgcolor: cfg.bg, borderRadius: 2, p: 1, minHeight: 200, overflowY: 'auto' }}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {items.map((item) => (
            <ContentCard key={item.id} item={item} onClick={onCardClick} />
          ))}
        </SortableContext>
      </Box>
    </Box>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function ContentBoard({ items = [], events = [], onRefresh }) {
  const [activeId, setActiveId] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newItem, setNewItem] = useState({ title: '', channels: [], contentType: '', ownerName: '' });
  // Filters
  const [filterTag, setFilterTag] = useState(null);
  const [filterOwner, setFilterOwner] = useState('');
  const [filterChannel, setFilterChannel] = useState('');

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Derive unique tags and owners from items
  const allTags = useMemo(() => [...new Set(items.map((i) => i.campaignTag).filter(Boolean))], [items]);
  const allOwners = useMemo(() => [...new Set(items.map((i) => i.ownerName).filter(Boolean))], [items]);

  // Filtered items
  const filtered = useMemo(() => {
    return items.filter((i) => {
      if (i.status === 'ARCHIVED') return false;
      if (filterTag && i.campaignTag !== filterTag) return false;
      if (filterOwner && i.ownerName !== filterOwner) return false;
      if (filterChannel && !(i.channels || []).includes(filterChannel)) return false;
      return true;
    });
  }, [items, filterTag, filterOwner, filterChannel]);

  const columnItems = useMemo(() => {
    const map = {};
    KANBAN_COLUMNS.forEach((col) => { map[col] = []; });
    filtered.forEach((item) => {
      if (map[item.status]) map[item.status].push(item);
    });
    return map;
  }, [filtered]);

  const activeItem = activeId ? items.find((i) => i.id === activeId) : null;

  const handleDragStart = useCallback((event) => setActiveId(event.active.id), []);

  const handleDragEnd = useCallback(async (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    // Determine target column: over.id could be a column id or an item id
    let targetCol = KANBAN_COLUMNS.includes(over.id) ? over.id : null;
    if (!targetCol) {
      const overItem = items.find((i) => i.id === over.id);
      if (overItem) targetCol = overItem.status;
    }

    const draggedItem = items.find((i) => i.id === active.id);
    if (!draggedItem || !targetCol || draggedItem.status === targetCol) return;

    try {
      const res = await authFetch(`${BACKEND_URL}/Api/marketing/content-items/${active.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: targetCol }),
      });
      if (!res.ok) throw new Error('Status update mislukt');
      toast.success(`Status gewijzigd naar ${STATUS_CONFIG[targetCol]?.label}`);
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  }, [items, onRefresh]);

  const handleCardClick = useCallback((item) => {
    setDetailItem(item);
    setDrawerOpen(true);
  }, []);

  const handleAdd = async () => {
    if (!newItem.title.trim()) { toast.error('Titel is verplicht'); return; }
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/marketing/content-items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newItem, status: 'IDEA' }),
      });
      if (!res.ok) throw new Error('Aanmaken mislukt');
      toast.success('Content item aangemaakt');
      setAddOpen(false);
      setNewItem({ title: '', channels: [], contentType: '', ownerName: '' });
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Box>
      {/* Filter bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Autocomplete
          options={allTags} value={filterTag} onChange={(_, v) => setFilterTag(v)}
          renderInput={(params) => <TextField {...params} label="Campagne" size="small" sx={{ minWidth: 180 }} />}
          size="small" freeSolo
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Eigenaar</InputLabel>
          <Select value={filterOwner} label="Eigenaar" onChange={(e) => setFilterOwner(e.target.value)}>
            <MenuItem value="">Alle</MenuItem>
            {allOwners.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Kanaal</InputLabel>
          <Select value={filterChannel} label="Kanaal" onChange={(e) => setFilterChannel(e.target.value)}>
            <MenuItem value="">Alle</MenuItem>
            {CHANNELS.map((ch) => <MenuItem key={ch} value={ch}>{ch}</MenuItem>)}
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
          Nieuw item
        </Button>
      </Box>

      {/* Board */}
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 2 }}>
          {KANBAN_COLUMNS.map((col) => (
            <KanbanColumn key={col} columnId={col} items={columnItems[col]} onCardClick={handleCardClick} />
          ))}
        </Box>
        <DragOverlay>
          {activeItem ? <ContentCard item={activeItem} overlay /> : null}
        </DragOverlay>
      </DndContext>

      {/* Add dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nieuw content item</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Titel" required value={newItem.title} onChange={(e) => setNewItem((p) => ({ ...p, title: e.target.value }))} />
          <FormControl>
            <InputLabel>Kanaal</InputLabel>
            <Select multiple value={newItem.channels} label="Kanaal" onChange={(e) => setNewItem((p) => ({ ...p, channels: e.target.value }))}>
              {CHANNELS.map((ch) => <MenuItem key={ch} value={ch}>{ch}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>Content type</InputLabel>
            <Select value={newItem.contentType} label="Content type" onChange={(e) => setNewItem((p) => ({ ...p, contentType: e.target.value }))}>
              {CONTENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Eigenaar naam" value={newItem.ownerName} onChange={(e) => setNewItem((p) => ({ ...p, ownerName: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Annuleren</Button>
          <Button variant="contained" onClick={handleAdd}>Aanmaken</Button>
        </DialogActions>
      </Dialog>

      {/* Detail drawer */}
      <ContentItemDetail
        open={drawerOpen}
        item={detailItem}
        events={events}
        onClose={() => { setDrawerOpen(false); setDetailItem(null); }}
        onSaved={onRefresh}
      />
    </Box>
  );
}
